/**
 * Pure helpers for linking a slice in one phase volume to the anatomically
 * nearest slice in another.
 *
 * We align by WORLD POSITION, not by slice index. Phases of one study share a
 * frame of reference, so each slice's projection onto the slice normal (signed
 * mm — the `z` you see in ImagePositionPatient for an axial scan) is directly
 * comparable across phases. The linked slice is simply the target slice whose
 * world position is closest to the source slice's world position.
 *
 * This replaces the older slice-0-anchored index math. Anchoring at an endpoint
 * (slice 0 or the last slice) only works if every phase reconstructs over the
 * exact same cranio-caudal range — which is false in practice: e.g. an arterial
 * phase covering just the liver shares slice 0 with neither the portal nor the
 * delayed phase, so equal-index (or equal-distance-from-slice-0) silently maps
 * liver onto pelvis. World position has no such assumption.
 *
 * No cornerstone or vtk imports here — inputs are plain numeric data so this
 * module is trivially unit-testable.
 */

export type Vec3 = readonly [number, number, number];

/**
 * Slice normal = rowCosines × columnCosines, derived from a 6-element
 * ImageOrientationPatient `[rx, ry, rz, cx, cy, cz]`. Returns a unit vector, or
 * null when IOP is missing/degenerate (zero-length cross product). For a
 * canonical axial scan (`[1,0,0,0,1,0]`) this is `[0,0,1]`, so projecting an
 * instance's IPP onto it just reads its patient-space Z.
 */
export function sliceNormalFromIop(iop: readonly number[] | null | undefined): Vec3 | null {
  if (!iop || iop.length < 6) return null;
  const r: [number, number, number] = [iop[0], iop[1], iop[2]];
  const c: [number, number, number] = [iop[3], iop[4], iop[5]];
  if ([...r, ...c].some((v) => typeof v !== "number" || !Number.isFinite(v))) return null;
  const n: [number, number, number] = [
    r[1] * c[2] - r[2] * c[1],
    r[2] * c[0] - r[0] * c[2],
    r[0] * c[1] - r[1] * c[0],
  ];
  const len = Math.hypot(n[0], n[1], n[2]);
  if (!Number.isFinite(len) || len < 1e-9) return null;
  return [n[0] / len, n[1] / len, n[2] / len];
}

/**
 * Signed world position (mm) of one instance along the slice normal — the
 * scalar that orders slices anatomically and is comparable across phases that
 * share a frame of reference.
 *
 * Prefers `ImagePositionPatient · normal` (the real geometry Cornerstone and
 * annotation placement use). Falls back to the backend's precomputed
 * `z_position` (defined as `IPP · normal` by construction) when IPP or the
 * normal is unavailable. Returns null when neither is usable — the caller skips
 * that slice explicitly rather than guessing a position (no silent fallback).
 */
export function instanceWorldZ(
  ipp: readonly number[] | null | undefined,
  zPosition: number | null | undefined,
  normal: Vec3 | null,
): number | null {
  if (normal && ipp && ipp.length >= 3) {
    const x = ipp[0];
    const y = ipp[1];
    const z = ipp[2];
    if (typeof x === "number" && typeof y === "number" && typeof z === "number") {
      const proj = x * normal[0] + y * normal[1] + z * normal[2];
      if (Number.isFinite(proj)) return proj;
    }
  }
  if (typeof zPosition === "number" && Number.isFinite(zPosition)) return zPosition;
  return null;
}

/**
 * List-index of the slice whose world position is nearest to `sourceWorldZ`.
 *
 * Linear scan: direction- and spacing-agnostic (handles reversed-IPP scans and
 * non-uniform spacing without special cases), and a series is at most ~1k
 * slices scanned a few times per scroll tick — negligible. Entries that are
 * null (slices with no usable world position) are ignored.
 *
 * Clamping is implicit: when the source's world position lies beyond the
 * target's coverage (e.g. scrolling a full-abdomen phase into the pelvis where
 * a liver-only phase has no slices), the nearest in-range slice — the target's
 * edge — wins. That is the "park at the nearest edge" behavior, not a skip.
 *
 * Returns null only when no target slice has a usable world position.
 */
export function nearestSliceByWorldZ(
  worldZ: readonly (number | null)[],
  sourceWorldZ: number,
): number | null {
  let best = -1;
  let bestDist = Infinity;
  for (let i = 0; i < worldZ.length; i++) {
    const z = worldZ[i];
    if (z == null) continue;
    const dist = Math.abs(z - sourceWorldZ);
    if (dist < bestDist) {
      bestDist = dist;
      best = i;
    }
  }
  return best < 0 ? null : best;
}
